import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { dropAllRounds } from "../lib/outbox.js";
import { Fox } from "../components/Fox.js";
import { SettingsPanel } from "../components/SettingsPanel.js";
import { Banner, Button, ButtonLink, Card } from "../components/ui.js";
import { auth } from "../copy/auth.js";
import { errors } from "../copy/errors.js";
import { useSession } from "../session.js";

interface MePayload {
  user: { handle: string; display_name: string };
  progress: {
    learned: number;
    due_today: number;
    due_by_lang: { en: number; fr: number };
    lists: number;
    streak: number;
  };
}

export function MeScreen() {
  const navigate = useNavigate();
  const { user, membership, setUser, refresh } = useSession();
  const [me, setMe] = useState<MePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void refresh();
    void api<MePayload>("/api/me").then(setMe);
  }, []);

  const logout = async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch (err) {
      // A session that is already over needs no logout. Anything else (no
      // connection) leaves the cookie valid, so staying logged in is honest.
      if (!(err instanceof ApiError && err.status === 401)) return;
    }
    setUser(null);
    navigate("/welcome");
  };

  // A wrong join code should cost a tap to undo, not the whole account.
  const leave = async (classId: string) => {
    if (!membership) return;
    if (!window.confirm(auth.me.leaveConfirm(membership.name))) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/classes/${classId}/members/me`, { method: "DELETE" });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = window.confirm(auth.me.deleteConfirm);
    if (!ok) return;
    // A failure here used to be silent: the button did nothing at all, and the
    // account was still there (issue #17).
    setBusy(true);
    setError(null);
    try {
      await api("/api/me", { method: "DELETE" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
      return;
    } finally {
      setBusy(false);
    }
    // A round still waiting would only be refused: the account is gone.
    if (user) await dropAllRounds(user.id);
    setUser(null);
    navigate("/welcome");
  };

  // «heute dran» is a number you can act on (issue #49): it opens the round,
  // in the language with more words waiting.
  const due = me?.progress.due_by_lang;
  const dueLink = due && me.progress.due_today > 0 ? `/practice/due/${due.en >= due.fr ? "en" : "fr"}` : null;
  const streak = me?.progress.streak ?? 0;
  const stats = [
    { value: me?.progress.learned, label: auth.me.sitting(me?.progress.learned ?? 0), to: null },
    { value: me?.progress.due_today, label: auth.me.dueToday, to: dueLink },
    { value: me ? streak : undefined, label: auth.me.streak(streak), to: null },
    { value: me?.progress.lists, label: auth.me.lists(me?.progress.lists ?? 0), to: null },
  ];

  return (
    <div className="space-y-8 lg:space-y-10">
      <header className="flex flex-col items-center gap-3 text-center sm:flex-row sm:gap-5 sm:text-left">
        <Fox mood="sleep" className="h-20 w-20 shrink-0" />
        <div className="min-w-0">
          <h1 className="text-3xl break-words sm:text-4xl">{user?.display_name}</h1>
          <p className="text-ink-soft">{auth.me.loginHint}</p>
        </div>
      </header>

      <ul className="grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        {stats.map(({ value, label, to }) => {
          const body = (
            <>
              <strong className="block font-display text-3xl sm:text-4xl">{value ?? "–"}</strong>
              <span className={`mt-1 block text-sm ${to ? "font-semibold text-fox-ink" : "text-ink-soft"}`}>
                {label}
                {to ? " →" : ""}
              </span>
            </>
          );
          return (
            <Card as="li" key={label} className={to ? "transition-shadow hover:shadow-lg" : ""}>
              {to ? (
                <Link
                  to={to}
                  aria-label={auth.me.dueTodayAria(value ?? 0)}
                  className="block rounded-3xl px-3 py-5 text-center sm:py-7"
                >
                  {body}
                </Link>
              ) : (
                <div className="px-3 py-5 text-center sm:py-7">{body}</div>
              )}
            </Card>
          );
        })}
      </ul>

      {/* «Wiederholen» over several lists (issue #101). Always there: the
          count above is own lists only, and class lists can be reviewed too. */}
      <div className="sm:max-w-sm">
        <ButtonLink to="/review" variant="ghost">
          {auth.me.review}
        </ButtonLink>
      </div>

      <section className="space-y-3 sm:max-w-sm">
        <h2 className="text-xl">{auth.me.class}</h2>
        {membership ? (
          <p className="text-ink-soft">{membership.name}</p>
        ) : (
          <p className="text-ink-soft">{auth.me.noClass}</p>
        )}
        {membership?.role === "admin" ? (
          <ButtonLink to="/class">{auth.me.class}</ButtonLink>
        ) : null}
        {membership?.role === "member" ? (
          <Button variant="ghost" disabled={busy} onClick={() => void leave(membership.id)}>
            {auth.me.leaveClass}
          </Button>
        ) : null}
        {!membership && (user?.role === "adult" || user?.role === "operator") ? (
          <ButtonLink to="/class">{auth.me.createClass}</ButtonLink>
        ) : null}
        {!membership && user?.role === "learner" ? (
          <>
            <ButtonLink to="/join">{auth.me.joinClass}</ButtonLink>
            <ButtonLink to="/class" variant="ghost">
              {auth.me.haveInvite}
            </ButtonLink>
          </>
        ) : null}
      </section>

      <SettingsPanel />

      <section className="space-y-3 sm:max-w-sm">
        <h2 className="text-xl">{auth.me.account}</h2>
        {error ? <Banner>{error}</Banner> : null}
        <Button variant="ghost" onClick={() => void logout()}>
          {auth.me.logout}
        </Button>
        <button
          type="button"
          disabled={busy}
          className="min-h-12 w-full rounded-2xl px-4 text-sm font-semibold text-rose underline-offset-4 hover:underline disabled:opacity-50"
          onClick={() => void remove()}
        >
          {auth.me.delete}
        </button>
      </section>
    </div>
  );
}
